// =============================================================================
// Calendar Utilities — Shared date range computation
// =============================================================================
// PURPOSE: Extracted from CalendarPage so prefetch hooks can compute
// the same date ranges without importing the page component.

import { startOfDayInTZ, endOfDayInTZ } from "../lib/timezone";
import type { CalendarView } from "../types/calendar";

/** Variable-day view bounds. The slider and every range/column calc clamp to
 *  these so a bad localStorage value or a stray drag can never render 0 or 40
 *  columns. 10 is the practical ceiling before columns get unreadable even with
 *  horizontal scroll. */
export const MIN_DAY_COUNT = 1;
export const MAX_DAY_COUNT = 10;

/** Clamp + round any candidate day-count into [MIN_DAY_COUNT, MAX_DAY_COUNT]. */
export function clampDayCount(n: number): number {
  if (!Number.isFinite(n)) return 4;
  return Math.max(MIN_DAY_COUNT, Math.min(MAX_DAY_COUNT, Math.round(n)));
}

/** How many day columns a view shows. Presets are fixed; "days" reads the
 *  clamped dayCount; "month" returns 0 (it uses its own 6-week grid, not the
 *  timegrid columns). ONE definition so the fetch range and the rendered
 *  columns can never disagree. */
export function getViewDayCount(view: CalendarView, dayCount?: number): number {
  switch (view) {
    case "day":
      return 1;
    case "4day":
      return 4;
    case "week":
      return 7;
    case "days":
      return clampDayCount(dayCount ?? 4);
    case "month":
      return 0;
  }
}

/**
 * Compute the UTC date range for a given calendar view and date,
 * based on the visible calendar dates in the org's timezone.
 *
 * - day: single day
 * - 4day: current day + 3 more
 * - week: Sunday–Saturday of the containing week
 * - days: current day + (dayCount - 1) more (variable 1–10; anchored at `date`,
 *   like 4day). lookback flips it to end at `date` and start dayCount-1 back.
 * - month: full calendar grid (extends to fill 6-week grid edges)
 */
export function computeDateRange(
  date: Date,
  view: CalendarView,
  tz: string,
  lookback?: boolean,
  dayCount?: number
): { rangeStart: Date; rangeEnd: Date } {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  let startY = y,
    startM = m,
    startD = d;
  let endY = y,
    endM = m,
    endD = d;

  switch (view) {
    case "day":
      // single day
      break;
    case "4day": {
      if (lookback) {
        const start = new Date(y, m, d - 3);
        startY = start.getFullYear();
        startM = start.getMonth();
        startD = start.getDate();
      } else {
        const end = new Date(y, m, d + 3);
        endY = end.getFullYear();
        endM = end.getMonth();
        endD = end.getDate();
      }
      break;
    }
    case "days": {
      // Variable 1–10 days, anchored at `date` (same shape as 4day). The span
      // is (count - 1) because `date` itself is one of the columns.
      const span = getViewDayCount("days", dayCount) - 1;
      if (lookback) {
        const start = new Date(y, m, d - span);
        startY = start.getFullYear();
        startM = start.getMonth();
        startD = start.getDate();
      } else {
        const end = new Date(y, m, d + span);
        endY = end.getFullYear();
        endM = end.getMonth();
        endD = end.getDate();
      }
      break;
    }
    case "week": {
      if (lookback) {
        const start = new Date(y, m, d - 6);
        startY = start.getFullYear();
        startM = start.getMonth();
        startD = start.getDate();
      } else {
        const dow = date.getDay();
        const start = new Date(y, m, d - dow);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
        startY = start.getFullYear();
        startM = start.getMonth();
        startD = start.getDate();
        endY = end.getFullYear();
        endM = end.getMonth();
        endD = end.getDate();
      }
      break;
    }
    case "month": {
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      // Extend to calendar grid edges
      const startDow = first.getDay();
      const start = new Date(first.getFullYear(), first.getMonth(), first.getDate() - startDow);
      const endDow = last.getDay();
      const end =
        endDow < 6
          ? new Date(last.getFullYear(), last.getMonth(), last.getDate() + (6 - endDow))
          : last;
      startY = start.getFullYear();
      startM = start.getMonth();
      startD = start.getDate();
      endY = end.getFullYear();
      endM = end.getMonth();
      endD = end.getDate();
      break;
    }
  }

  return {
    rangeStart: startOfDayInTZ(startY, startM, startD, tz),
    rangeEnd: endOfDayInTZ(endY, endM, endD, tz),
  };
}
